import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { ordersAPI, referralsAPI } from '../api';
import { ChevronLeft, MapPin, Calendar, Check, ShieldCheck, Zap, Clock, Leaf, AlertTriangle, Star, Tag, X } from 'lucide-react';

// Trial Mode comes from GET /api/payments/config (env-specific). Fail-safe: ON until loaded.

const DELIVERY_TYPES = [
  { 
    id: 'urgent', 
    label: 'Urgent', 
    icon: Zap,
    days: { min: 3, max: 5 }, 
    charge: 499, 
    description: '3-5 days delivery',
    tagColor: '#991B1B',
    tagBg: 'rgba(153, 27, 27, 0.08)'
  },
  { 
    id: 'standard', 
    label: 'Standard', 
    icon: Clock,
    days: { min: 7, max: 10 }, 
    charge: 0, 
    description: '7-10 days delivery',
    tagColor: '#D4AF37',
    tagBg: 'rgba(212, 175, 55, 0.08)'
  },
  { 
    id: 'flexible', 
    label: 'Flexible', 
    icon: Leaf,
    days: { min: 10, max: 15 }, 
    charge: -100, 
    description: '10-15 days (Save ₹100)',
    tagColor: '#166534',
    tagBg: 'rgba(22, 101, 52, 0.08)'
  },
];

const Checkout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const orderData = location.state || {};
  
  const [formData, setFormData] = useState({
    address: '',
    city: '',
    pincode: '',
    deliveryDate: '',
    deliveryType: 'standard',
    notes: '',
    paymentMethod: 'cod',
  });

  const [errors, setErrors] = useState({});

  const selectedDeliveryType = DELIVERY_TYPES.find(d => d.id === formData.deliveryType);

  const minDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + (selectedDeliveryType?.days.min || 7));
    return d.toISOString().split('T')[0];
  }, [selectedDeliveryType]);

  const maxDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() + (selectedDeliveryType?.days.max || 10));
    return d.toISOString().split('T')[0];
  }, [selectedDeliveryType]);

  const basePrice = parseInt(orderData.basePrice?.replace(/[^\d]/g, '') || 999);
  const customizationPrice = orderData.customizationPrice || 0;
  const deliveryFee = 99;
  const urgentCharge = selectedDeliveryType?.charge || 0;
  const subTotal = Math.max(0, basePrice + customizationPrice + deliveryFee + urgentCharge);

  // Coupon state
  const [couponInput, setCouponInput] = useState('');
  const [appliedCoupon, setAppliedCoupon] = useState(null); // {code, discount_amount, min_order}
  const [couponError, setCouponError] = useState('');
  const [couponChecking, setCouponChecking] = useState(false);

  const couponDiscount = appliedCoupon ? appliedCoupon.discount_amount : 0;
  const totalPrice = Math.max(0, subTotal - couponDiscount);

  const applyCoupon = async () => {
    setCouponError('');
    const code = couponInput.trim().toUpperCase();
    if (!code) return;
    setCouponChecking(true);
    try {
      const res = await referralsAPI.applyCoupon(code, subTotal);
      setAppliedCoupon({ code: res.code, discount_amount: res.discount_amount, min_order: res.min_order });
      setCouponInput('');
    } catch (err) {
      setCouponError(err.message);
    } finally {
      setCouponChecking(false);
    }
  };

  const removeCoupon = () => { setAppliedCoupon(null); setCouponError(''); };


  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleDeliveryTypeChange = (typeId) => {
    setFormData(prev => ({ ...prev, deliveryType: typeId, deliveryDate: '' }));
    setErrors(prev => ({ ...prev, deliveryDate: '' }));
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.address.trim()) newErrors.address = 'Address is required';
    if (!formData.city.trim()) newErrors.city = 'City is required';
    if (!formData.pincode.trim()) newErrors.pincode = 'Pincode is required';
    if (!formData.deliveryDate) newErrors.deliveryDate = 'Delivery date is required';
    if (!orderData.category) newErrors.category = 'Please select a garment before placing your order.';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const [paymentProcessing, setPaymentProcessing] = useState(false);
  const [paymentConfig, setPaymentConfig] = useState(null);
  const TRIAL_MODE = paymentConfig?.trial_mode !== false;

  // Fetch payment config on mount (advance_percent + razorpay_configured)
  useEffect(() => {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/config`)
      .then(r => r.json()).then(setPaymentConfig).catch(() => setPaymentConfig({ trial_mode: true, razorpay_configured: false, advance_percent: 50 }));
  }, []);

  // Load Razorpay script on mount (for when user clicks Pay)
  useEffect(() => {
    if (document.getElementById('razorpay-sdk')) return;
    const s = document.createElement('script');
    s.src = 'https://checkout.razorpay.com/v1/checkout.js';
    s.id = 'razorpay-sdk';
    s.async = true;
    document.body.appendChild(s);
  }, []);

  const handleSubmit = async () => {
    if (!validateForm()) return;
    setPaymentProcessing(true);

    const orderPayload = {
      product: orderData.categoryName || 'Custom Garment',
      category: orderData.category || '',
      delivery_type: formData.deliveryType,
      delivery_date: formData.deliveryDate,
      measurements: orderData.measurements || {},
      body_measurements: orderData.body_measurements || null,
      ease_applied: orderData.ease_applied || null,
      measurement_provenance: orderData.measurement_provenance || null,
      customizations: orderData.selectionDetails || orderData.customizations || {},
      address: formData.address,
      city: formData.city,
      pincode: formData.pincode,
      payment_method: 'razorpay_advance',
      notes: formData.notes,
      base_price: basePrice,
      customization_price: customizationPrice,
      delivery_fee: deliveryFee,
      urgent_charge: urgentCharge,
      coupon_code: appliedCoupon?.code || null,
      suggested_size: orderData.suggestedSize || null,
      fit_confidence: orderData.fitConfidence || null,
      measurement_schema: orderData.measurementSchema || null,
    };

    let created;
    try {
      created = await ordersAPI.create(orderPayload);
    } catch (err) {
      setPaymentProcessing(false);
      alert('Could not create order. Please try again.');
      return;
    }

    if (created.trial_order !== false) {
      setPaymentProcessing(false);
      navigate('/payment-result', {
        state: {
          success: false,
          orderId: created.order_id,
          reason: 'Trial mode — your order is placed. No payment collected now.',
          totalPrice: created.total_price,
        }
      });
      return;
    }

    // If Razorpay not configured → show pending status (user will pay later)
    if (!paymentConfig?.razorpay_configured) {
      setPaymentProcessing(false);
      navigate('/payment-result', {
        state: {
          success: false,
          orderId: created.order_id,
          reason: 'Razorpay not configured. Your order is saved as pending payment.',
          totalPrice: created.total_price,
          advanceAmount: created.advance_amount,
        }
      });
      return;
    }

    // Create Razorpay order (advance amount)
    let rzpOrder;
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/create-order/${created.order_id}`, {
        method: 'POST', credentials: 'include',
      });
      if (!resp.ok) throw new Error((await resp.json()).detail || 'Payment setup failed');
      rzpOrder = await resp.json();
    } catch (err) {
      setPaymentProcessing(false);
      navigate('/payment-result', { state: { success: false, orderId: created.order_id, reason: err.message } });
      return;
    }

    // Keep the bank's decline reason so the result page shows it instead of "Payment cancelled".
    let lastFailure = null;
    // Open Razorpay modal
    const rzp = new window.Razorpay({
      key: rzpOrder.razorpay_key_id,
      amount: rzpOrder.amount_paise,
      currency: rzpOrder.currency,
      name: "Lunora's Premium Fashion",
      description: `Advance (${rzpOrder.advance_percent}%) for ${created.order_id}`,
      order_id: rzpOrder.razorpay_order_id,
      prefill: {
        name: user?.name || '',
        email: user?.email || '',
        contact: user?.phone || '',
      },
      theme: { color: '#D4AF37' },
      handler: async (response) => {
        try {
          const verifyResp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/verify`, {
            method: 'POST', credentials: 'include',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              order_id: created.order_id,
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
            }),
          });
          const result = await verifyResp.json();
          setPaymentProcessing(false);
          if (verifyResp.ok && result.success) {
            navigate('/payment-result', {
              state: {
                success: true,
                orderId: created.order_id,
                totalPrice: created.total_price,
                advanceAmount: created.advance_amount,
                balanceAmount: created.balance_amount,
                notifications: result.notifications,
              }
            });
          } else {
            navigate('/payment-result', { state: { success: false, orderId: created.order_id, reason: result.detail || 'Verification failed' } });
          }
        } catch (err) {
          setPaymentProcessing(false);
          navigate('/payment-result', { state: { success: false, orderId: created.order_id, reason: err.message } });
        }
      },
      modal: {
        ondismiss: () => {
          setPaymentProcessing(false);
          navigate('/payment-result', { state: { success: false, orderId: created.order_id, reason: lastFailure || 'Payment cancelled' } });
        }
      }
    });
    rzp.on('payment.failed', (resp) => { lastFailure = resp?.error?.description || resp?.error?.reason || 'Payment failed'; });
    rzp.open();
  };

  return (
    <div 
      data-testid="checkout-page"
      className="min-h-screen flex flex-col"
      style={{ backgroundColor: '#FAF9F6', fontFamily: 'Outfit, sans-serif' }}
    >
      {/* Header */}
      <div 
        className="sticky top-0 z-50 flex items-center justify-between px-4 py-4 border-b"
        style={{ backgroundColor: 'rgba(250, 249, 246, 0.9)', backdropFilter: 'blur(12px)', borderColor: '#E5E5E5' }}
      >
        <button data-testid="back-btn" onClick={() => navigate(-1)} className="p-2 -ml-2">
          <ChevronLeft className="w-6 h-6" style={{ color: '#0F0F0F' }} strokeWidth={1.5} />
        </button>
        <h1 className="text-lg" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>Checkout</h1>
        <div className="w-10" />
      </div>

      <div className="flex-1 px-6 py-8">
        {/* Delivery Address */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <MapPin className="w-5 h-5" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
            <p className="text-xs tracking-[0.15em] uppercase" style={{ color: '#595959' }}>Delivery Address</p>
          </div>
          <div className="space-y-4">
            <div>
              <input
                data-testid="address-input"
                type="text"
                name="address"
                value={formData.address}
                onChange={handleChange}
                placeholder="Full Address"
                className="w-full px-0 py-3 text-sm bg-transparent border-0 border-b focus:outline-none focus:border-[#0F0F0F]"
                style={{ borderColor: errors.address ? '#991B1B' : '#E5E5E5', color: '#0F0F0F' }}
              />
              {errors.address && <p className="text-xs mt-1" style={{ color: '#991B1B' }}>{errors.address}</p>}
            </div>
            <div className="flex gap-4">
              <div className="flex-1">
                <input
                  data-testid="city-input"
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  placeholder="City"
                  className="w-full px-0 py-3 text-sm bg-transparent border-0 border-b focus:outline-none focus:border-[#0F0F0F]"
                  style={{ borderColor: errors.city ? '#991B1B' : '#E5E5E5', color: '#0F0F0F' }}
                />
              </div>
              <div className="flex-1">
                <input
                  data-testid="pincode-input"
                  type="text"
                  name="pincode"
                  value={formData.pincode}
                  onChange={handleChange}
                  placeholder="Pincode"
                  className="w-full px-0 py-3 text-sm bg-transparent border-0 border-b focus:outline-none focus:border-[#0F0F0F]"
                  style={{ borderColor: errors.pincode ? '#991B1B' : '#E5E5E5', color: '#0F0F0F' }}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Delivery Type Selection */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-4">
            <Zap className="w-5 h-5" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
            <p className="text-xs tracking-[0.15em] uppercase" style={{ color: '#595959' }}>Delivery Speed</p>
          </div>
          <div className="space-y-3">
            {DELIVERY_TYPES.map((type) => {
              const isSelected = formData.deliveryType === type.id;
              const TypeIcon = type.icon;
              return (
                <button
                  key={type.id}
                  data-testid={`delivery-type-${type.id}`}
                  onClick={() => handleDeliveryTypeChange(type.id)}
                  className="w-full p-4 flex items-center gap-4 border transition-all bg-white"
                  style={{ borderColor: isSelected ? '#0F0F0F' : '#E5E5E5' }}
                >
                  <div 
                    className="w-10 h-10 flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: type.tagBg }}
                  >
                    <TypeIcon className="w-5 h-5" style={{ color: type.tagColor }} strokeWidth={1.5} />
                  </div>
                  <div className="flex-1 text-left">
                    <div className="flex items-center gap-2">
                      <p className="text-sm font-medium" style={{ color: '#0F0F0F' }}>{type.label}</p>
                      {type.id === 'urgent' && (
                        <span className="text-[10px] px-2 py-0.5 tracking-[0.05em] uppercase" style={{ backgroundColor: type.tagBg, color: type.tagColor }}>
                          +₹{type.charge}
                        </span>
                      )}
                      {type.id === 'flexible' && (
                        <span className="text-[10px] px-2 py-0.5 tracking-[0.05em] uppercase" style={{ backgroundColor: type.tagBg, color: type.tagColor }}>
                          Save ₹{Math.abs(type.charge)}
                        </span>
                      )}
                    </div>
                    <p className="text-xs" style={{ color: '#595959' }}>{type.description}</p>
                  </div>
                  {isSelected && <Check className="w-5 h-5 flex-shrink-0" style={{ color: '#166534' }} strokeWidth={2} />}
                </button>
              );
            })}
          </div>
          {formData.deliveryType === 'urgent' && (
            <div className="mt-3 flex items-start gap-2 p-3" style={{ backgroundColor: 'rgba(153, 27, 27, 0.05)' }}>
              <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" style={{ color: '#991B1B' }} strokeWidth={1.5} />
              <p className="text-xs" style={{ color: '#991B1B' }}>
                Urgent orders are prioritized. Additional ₹499 charge applies for express tailoring.
              </p>
            </div>
          )}
        </div>

        {/* Delivery Date */}
        <div className="mb-8">
          <div className="flex items-center gap-2 mb-2">
            <Calendar className="w-5 h-5" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
            <p className="text-xs tracking-[0.15em] uppercase" style={{ color: '#595959' }}>Required Delivery Date</p>
          </div>
          <p className="text-xs mb-3" style={{ color: '#999' }}>
            Select a date between {new Date(minDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })} - {new Date(maxDate).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' })}
          </p>
          <input
            data-testid="delivery-date-input"
            type="date"
            name="deliveryDate"
            value={formData.deliveryDate}
            onChange={handleChange}
            min={minDate}
            max={maxDate}
            className="w-full px-0 py-3 text-sm bg-transparent border-0 border-b focus:outline-none focus:border-[#0F0F0F]"
            style={{ borderColor: errors.deliveryDate ? '#991B1B' : '#E5E5E5', color: '#0F0F0F' }}
          />
          {errors.deliveryDate && <p className="text-xs mt-1" style={{ color: '#991B1B' }}>{errors.deliveryDate}</p>}
        </div>

        {/* Notes */}
        <div className="mb-8">
          <p className="text-xs tracking-[0.15em] uppercase mb-4" style={{ color: '#595959' }}>Special Instructions</p>
          <textarea
            data-testid="notes-input"
            name="notes"
            value={formData.notes}
            onChange={handleChange}
            placeholder="Any special requests..."
            rows={3}
            className="w-full px-4 py-3 text-sm bg-white border focus:outline-none focus:border-[#0F0F0F] resize-none"
            style={{ borderColor: '#E5E5E5', color: '#0F0F0F' }}
          />
        </div>

        {paymentConfig && TRIAL_MODE && (
          <div className="mb-8 p-3.5" data-testid="trial-mode-banner"
            style={{
              background: 'linear-gradient(135deg, rgba(212,175,55,0.08), rgba(229,178,185,0.08))',
              border: '1px solid rgba(212,175,55,0.45)',
            }}>
            <div className="flex items-start gap-2">
              <ShieldCheck className="w-3.5 h-3.5 mt-0.5" style={{ color: '#D4AF37' }} strokeWidth={1.6} />
              <div>
                <p className="text-[10px] tracking-[0.18em] uppercase" style={{ color: '#7A6420', fontWeight: 700 }}>
                  Trial Mode · No Payment
                </p>
                <p className="text-[11.5px] mt-0.5" style={{ color: '#0F0F0F', lineHeight: 1.5 }}>
                  We are onboarding first customers. Place your order now — payment
                  will be collected on delivery once we go live.
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Coupon */}
        <div className="mb-4">
          {orderData.fitConfidence != null && (
            <div data-testid="fit-verified-badge" className="mb-3 p-2.5 flex items-center gap-2"
              style={{ backgroundColor: 'rgba(22,101,52,0.06)', border: '1px solid #166534' }}>
              <ShieldCheck className="w-3.5 h-3.5" style={{ color: '#166534' }} strokeWidth={1.5} />
              <p className="text-[11px]" style={{ color: '#166534' }}>
                Measurements verified · {orderData.suggestedSize ? `Size ${orderData.suggestedSize} · ` : ''}{orderData.fitConfidence}% fit confidence
              </p>
            </div>
          )}
          <div className="flex items-center gap-2 mb-3">
            <Tag className="w-4 h-4" style={{ color: '#D4AF37' }} strokeWidth={1.5} />
            <p className="text-xs tracking-[0.15em] uppercase" style={{ color: '#595959' }}>Have a Coupon?</p>
          </div>
          {appliedCoupon ? (
            <div data-testid="applied-coupon-row" className="flex items-center justify-between p-3 border-2"
              style={{ borderColor: '#166534', backgroundColor: 'rgba(22,101,52,0.04)' }}>
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4" style={{ color: '#166534' }} strokeWidth={2} />
                <div>
                  <p className="text-[12px] font-medium" style={{ color: '#0F0F0F' }}>
                    {appliedCoupon.code} applied
                  </p>
                  <p className="text-[10px]" style={{ color: '#166534' }}>You save ₹{appliedCoupon.discount_amount}</p>
                </div>
              </div>
              <button data-testid="remove-coupon-btn" onClick={removeCoupon} className="p-1">
                <X className="w-4 h-4" style={{ color: '#595959' }} strokeWidth={1.5} />
              </button>
            </div>
          ) : (
            <div>
              <div className="flex gap-2">
                <input data-testid="coupon-input" type="text" value={couponInput}
                  onChange={e => { setCouponInput(e.target.value.toUpperCase()); setCouponError(''); }}
                  placeholder="Enter coupon code"
                  className="flex-1 px-3 py-2.5 text-[13px] bg-white border focus:outline-none focus:border-[#0F0F0F] uppercase tracking-wider"
                  style={{ borderColor: couponError ? '#991B1B' : '#E5E5E5', color: '#0F0F0F' }} />
                <button data-testid="apply-coupon-btn" onClick={applyCoupon} disabled={couponChecking || !couponInput}
                  className="px-4 text-[11px] tracking-[0.08em] uppercase text-white disabled:opacity-50"
                  style={{ backgroundColor: '#0F0F0F' }}>
                  {couponChecking ? '…' : 'Apply'}
                </button>
              </div>
              {couponError && <p data-testid="coupon-error" className="text-[10px] mt-1" style={{ color: '#991B1B' }}>{couponError}</p>}
              <p className="text-[10px] mt-1" style={{ color: '#BFBFBF' }}>
                Have a referral coupon? Enter it to save on this order.
              </p>
            </div>
          )}
        </div>

        {/* Order Summary */}
        <div className="bg-white border p-6 mb-6" style={{ borderColor: '#E5E5E5' }}>
          <p className="text-xs tracking-[0.15em] uppercase mb-4" style={{ color: '#595959' }}>Order Summary</p>
          <div className="space-y-3">
            <div className="flex justify-between text-sm">
              <span style={{ color: '#595959' }}>{orderData.categoryName || 'Product'}</span>
              <span style={{ color: '#0F0F0F' }}>₹{basePrice.toLocaleString('en-IN')}</span>
            </div>
            {customizationPrice > 0 && (
              <div className="flex justify-between text-sm">
                <span style={{ color: '#595959' }}>Customizations</span>
                <span style={{ color: '#0F0F0F' }}>+₹{customizationPrice.toLocaleString('en-IN')}</span>
              </div>
            )}
            <div className="flex justify-between text-sm">
              <span style={{ color: '#595959' }}>Delivery Fee</span>
              <span style={{ color: '#0F0F0F' }}>₹{deliveryFee}</span>
            </div>
            {urgentCharge !== 0 && (
              <div className="flex justify-between text-sm">
                <span style={{ color: urgentCharge > 0 ? '#991B1B' : '#166534' }}>
                  {urgentCharge > 0 ? 'Urgent Charge' : 'Flexible Discount'}
                </span>
                <span style={{ color: urgentCharge > 0 ? '#991B1B' : '#166534' }}>
                  {urgentCharge > 0 ? `+₹${urgentCharge}` : `-₹${Math.abs(urgentCharge)}`}
                </span>
              </div>
            )}
            {appliedCoupon && (
              <div className="flex justify-between text-sm" data-testid="coupon-line">
                <span style={{ color: '#166534' }}>Coupon ({appliedCoupon.code})</span>
                <span style={{ color: '#166534' }}>-₹{appliedCoupon.discount_amount}</span>
              </div>
            )}
            <div className="pt-3 border-t flex justify-between" style={{ borderColor: '#E5E5E5' }}>
              <span className="font-medium" style={{ color: '#0F0F0F' }}>Total</span>
              <span className="text-xl" style={{ fontFamily: 'Playfair Display, serif', color: '#0F0F0F' }}>₹{totalPrice}</span>
            </div>
          </div>
        </div>

        {/* Guarantee Banner */}
        <div className="bg-white border p-4 mb-4" style={{ borderColor: '#E5E5E5' }}>
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 flex items-center justify-center flex-shrink-0" style={{ backgroundColor: 'rgba(22,101,52,0.08)' }}>
              <ShieldCheck className="w-5 h-5" style={{ color: '#166534' }} strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-[13px] font-medium" style={{ color: '#0F0F0F' }}>Lunora's Guarantee</p>
              <p className="text-[11px]" style={{ color: '#595959' }}>Your order is protected</p>
            </div>
          </div>
          <div className="space-y-2 text-[11px]" style={{ color: '#595959' }}>
            <div className="flex items-center gap-2">
              <Check className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#166534' }} strokeWidth={2} />
              <span>Free alterations within 7 days of delivery</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#166534' }} strokeWidth={2} />
              <span>Full refund if order is cancelled before stitching</span>
            </div>
            <div className="flex items-center gap-2">
              <Check className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#166534' }} strokeWidth={2} />
              <span>Verified master tailor assigned to your order</span>
            </div>
          </div>
        </div>

        {/* Trust Badges Row */}
        <div className="flex items-center justify-center gap-4 mb-6 py-3 text-[9px]" style={{ color: '#595959' }}>
          <span className="flex items-center gap-1"><ShieldCheck className="w-3.5 h-3.5" style={{ color: '#166534' }} strokeWidth={1.5} />Secure</span>
          <span style={{ color: '#E5E5E5' }}>|</span>
          <span className="flex items-center gap-1"><Star className="w-3.5 h-3.5" style={{ color: '#D4AF37', fill: '#D4AF37' }} strokeWidth={1.5} />4.9 Rating</span>
          <span style={{ color: '#E5E5E5' }}>|</span>
          <span>25,000+ Orders</span>
        </div>
      </div>

      {/* Payment breakdown banner */}
      {paymentConfig && !TRIAL_MODE && (
        <div data-testid="payment-breakdown" className="mx-4 my-3 p-4 border" style={{ borderColor: '#D4AF37', backgroundColor: 'rgba(212,175,55,0.04)' }}>
          <div className="flex items-center justify-between mb-2">
            <p className="text-[10px] tracking-[0.14em] uppercase" style={{ color: '#D4AF37' }}>Payment Plan</p>
            <span className="text-[9px] px-2 py-0.5" style={{ backgroundColor: '#166534', color: '#FFF' }}>
              {paymentConfig.advance_percent}% ADVANCE
            </span>
          </div>
          <div className="space-y-1 text-[12px]" style={{ color: '#0F0F0F' }}>
            <div className="flex justify-between"><span style={{ color: '#595959' }}>Total Price</span><span>{'\u20B9'}{totalPrice.toLocaleString('en-IN')}</span></div>
            <div className="flex justify-between"><span style={{ color: '#D4AF37', fontWeight: 500 }}>Pay Now (Advance)</span>
              <span style={{ fontWeight: 600 }}>{'\u20B9'}{Math.round(totalPrice * paymentConfig.advance_percent / 100).toLocaleString('en-IN')}</span>
            </div>
            <div className="flex justify-between"><span style={{ color: '#595959' }}>Balance on Delivery</span>
              <span>{'\u20B9'}{(totalPrice - Math.round(totalPrice * paymentConfig.advance_percent / 100)).toLocaleString('en-IN')}</span>
            </div>
          </div>
          {!paymentConfig.razorpay_configured && (
            <p className="text-[9px] mt-2 flex items-center gap-1" style={{ color: '#991B1B' }}>
              <AlertTriangle className="w-3 h-3" strokeWidth={1.5} /> Razorpay not configured. Order will save as pending payment.
            </p>
          )}
        </div>
      )}

      {/* Bottom CTA */}
      <div className="sticky bottom-0 bg-white border-t px-4 py-3" style={{ borderColor: '#E5E5E5' }}>
        {errors.category && (
          <p data-testid="checkout-category-error" className="text-[11px] mb-2 text-center" style={{ color: '#991B1B' }}>
            {errors.category}{' '}
            <button data-testid="checkout-select-garment-btn" className="underline" onClick={() => navigate('/gender-selection')}>
              Select a garment
            </button>
          </p>
        )}
        <button
          data-testid="place-order-btn"
          onClick={handleSubmit}
          disabled={paymentProcessing || !paymentConfig}
          className="w-full py-4 text-white text-sm tracking-[0.08em] uppercase disabled:opacity-60"
          style={{ background: 'linear-gradient(135deg, #E5B2B9 0%, #D4AF37 100%)', fontWeight: 600 }}
        >
          {paymentProcessing ? 'Processing…' : !paymentConfig ? 'Loading…' : (
            !TRIAL_MODE && paymentConfig?.razorpay_configured
              ? <>Pay Advance {'\u2022'} {'\u20B9'}{Math.round(totalPrice * (paymentConfig?.advance_percent ?? 50) / 100).toLocaleString('en-IN')}</>
              : <>Place Order {'\u2022'} {'\u20B9'}{totalPrice.toLocaleString('en-IN')}</>
          )}
        </button>
        <p className="text-[9px] text-center mt-1.5" style={{ color: '#BFBFBF' }}>
          {!TRIAL_MODE && paymentConfig?.razorpay_configured ? `${paymentConfig?.advance_percent ?? 50}% advance · Balance on delivery · Secure Razorpay` : 'Free alterations · Secure payment · 7-day guarantee'}
        </p>
      </div>
    </div>
  );
};

export default Checkout;
